import { ButtonBaseActions } from '@mui/material/ButtonBase';
import { useCallback, useLayoutEffect, useState } from 'react';

export const useFocusManager = (container: HTMLElement, onFocusLeft?: () => void) => {
  const [nodes, setNodes] = useState<HTMLElement[]>([]);
  const [focusPointer, setFocusPointer] = useState<number | null>(null);

  const resetFocusIndex = useCallback(() => {
    setFocusPointer(null);
  }, []);

  const movePointer = useCallback(
    (pointer: number | null, reverse: boolean): number => {
      if (pointer === null && reverse) {
        return nodes.length - 1;
      } else if (pointer === null) {
        return 0;
      } else if (reverse) {
        return pointer - 1;
      } else {
        return pointer + 1;
      }
    },
    [nodes.length],
  );

  const focusButton = useCallback(
    (index: number) => {
      nodes[index]?.focus();
    },
    [nodes],
  );

  const focusNextButton = useCallback(
    (reverse = false): boolean => {
      const focusIndex = movePointer(focusPointer, reverse);

      if (nodes[focusIndex]) {
        focusButton(focusIndex);
        setFocusPointer(focusIndex);
        return true;
      } else {
        resetFocusIndex();
        onFocusLeft && onFocusLeft();
        return false;
      }
    },
    [focusButton, focusPointer, movePointer, nodes, resetFocusIndex, onFocusLeft],
  );

  const collectNode = (node: HTMLElement, index: number) => {
    nodes[index] = node;
    setNodes(nodes);
  };

  const collectActions = (_newActions: ButtonBaseActions, _index: number) => {
    // MUI v5 removed ButtonBase `action` callback; focus is handled via DOM refs.
  };

  const keyDownHandler = useCallback(
    (event) => {
      if (event.key === 'Tab' && focusNextButton(event.shiftKey)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [focusNextButton],
  );

  useLayoutEffect(() => {
    if (container) {
      container.addEventListener('keydown', keyDownHandler);
      container.addEventListener('focus', resetFocusIndex);

      return () => {
        container.removeEventListener('keydown', keyDownHandler);
        container.removeEventListener('focus', resetFocusIndex);
      };
    }
  }, [keyDownHandler, resetFocusIndex, container]);

  return { collectNode, collectActions };
};
